import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Assignment, Add, Save, Search, Clear } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useObraSocialRequisitos } from '../hooks/useObraSocialRequisitos';
import { obrasSocialesAPI, tiposEstudiosAPI } from '../../../core/api/api';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const EMPTY_FORM = { idObraSocial: '', idRequisitos: [], idTiposEstudio: [], idPlanes: [], adjuntoObligatorio: false };

// Agrupa: OS -> Plan -> TipoEstudio -> [requisitos]
const agrupar = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const osKey = `${r.idObraSocial}`;
    if (!map.has(osKey)) map.set(osKey, { obraSocial: r.obraSocial, idObraSocial: r.idObraSocial, grupos: new Map() });
    const os = map.get(osKey);
    const grupoKey = `${r.idTipoEstudio}__${r.idPlan ?? ''}`;
    if (!os.grupos.has(grupoKey)) {
      os.grupos.set(grupoKey, {
        tipoEstudio: r.tipoEstudio, idTipoEstudio: r.idTipoEstudio,
        plan: r.plan, idPlan: r.idPlan,
        requisitos: [],
      });
    }
    os.grupos.get(grupoKey).requisitos.push({ id: r.idRequisitoObraSocial, nombre: r.requisito, idRequisito: r.idRequisito, adjuntoObligatorio: r.adjuntoObligatorio });
  });
  return Array.from(map.values()).map((os) => ({ ...os, grupos: Array.from(os.grupos.values()) }));
};

const GrillaObraSocialRequisitos = () => {
  const { requisitos, loading, cargar, guardar, actualizar, eliminar } = useObraSocialRequisitos();
  const [obrasSociales, setObrasSociales] = useState([]);
  const [tiposEstudio, setTiposEstudio] = useState([]);
  const [requisitosOpts, setRequisitosOpts] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [filtros, setFiltros] = useState({ idObraSocial: '', idRequisito: '', idTipoEstudio: '' });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmEliminar, setConfirmEliminar] = useState(null); // { ids: [], label: '' }
  const [confirmPisar, setConfirmPisar] = useState(null); // { message, onConfirm }
  const [toast, setToast] = useState(null);

  const cargarCatalogos = useCallback(async () => {
    const [osRes, teRes, reqRes] = await Promise.all([
      obrasSocialesAPI.getObrasSociales(),
      tiposEstudiosAPI.getTiposEstudios(),
      obrasSocialesAPI.getRequisitos(),
    ]);
    setObrasSociales(osRes.data || []);
    setTiposEstudio(teRes.data || []);
    setRequisitosOpts(reqRes.data || []);
  }, []);

  useEffect(() => { cargarCatalogos(); cargar(); }, [cargarCatalogos, cargar]);

  const aplicarFiltros = useCallback((f) => {
    cargar({ idObraSocial: f.idObraSocial || undefined, idRequisito: f.idRequisito || undefined, idTipoEstudio: f.idTipoEstudio || undefined });
  }, [cargar]);

  const handleFiltroChange = (e) => setFiltros((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleBuscar = () => aplicarFiltros(filtros);
  const handleLimpiar = () => { setFiltros({ idObraSocial: '', idRequisito: '', idTipoEstudio: '' }); cargar(); };

  const agrupado = useMemo(() => agrupar(requisitos), [requisitos]);

  // ── Nuevo ──
  const abrirNuevo = () => { setForm(EMPTY_FORM); setPlanes([]); setModal({ mode: 'nuevo' }); };

  const handleObraSocialChange = async (idOS) => {
    setForm((f) => ({ ...f, idObraSocial: idOS, idPlanes: [], idRequisitos: [], idTiposEstudio: [] }));
    setPlanes(idOS ? (await obrasSocialesAPI.getPlanesPorObraSocial(idOS)).data || [] : []);
  };

  // Detectar combinaciones que ya existen para mostrar alerta
  const detectarExistentes = () => {
    if (!form.idObraSocial || form.idTiposEstudio.length === 0 || form.idRequisitos.length === 0) return [];
    const planesAGuardar = form.idPlanes.length > 0 ? form.idPlanes : [null];
    const existentes = [];
    for (const idTE of form.idTiposEstudio) {
      for (const idPlan of planesAGuardar) {
        for (const idReq of form.idRequisitos) {
          const existe = requisitos.find(r =>
            r.idObraSocial === Number(form.idObraSocial) &&
            r.idTipoEstudio === idTE &&
            (r.idPlan ?? null) === idPlan &&
            r.idRequisito === idReq
          );
          if (existe) existentes.push(existe);
        }
      }
    }
    return existentes;
  };

  // tipos de estudio donde YA existen todos los requisitos disponibles para esa OS
  const tiposEstudioDisponibles = useMemo(() => {
    if (!form.idObraSocial) return tiposEstudio;
    return tiposEstudio.filter((te) => {
      const reqExistentes = requisitos.filter(
        (r) => r.idObraSocial === Number(form.idObraSocial) && r.idTipoEstudio === te.idTipoEstudio
      ).map((r) => r.idRequisito);
      // mostrar si aún hay requisitos que no existen para este tipo
      return reqExistentes.length < requisitosOpts.length;
    });
  }, [tiposEstudio, requisitos, form.idObraSocial, requisitosOpts]);

  const modalBodyRef = React.useRef(null);

  const toggleMulti = (field, val) => {
    const scrollEl = modalBodyRef.current?.closest('.modal');
    const scrollTop = scrollEl?.scrollTop || 0;
    const num = Number(val);
    setForm((f) => {
      requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = scrollTop;
      });
      return { ...f, [field]: f[field].includes(num) ? f[field].filter((x) => x !== num) : [...f[field], num] };
    });
  };

  const toggleTodos = (field, opts, idField) => {
    const all = opts.map((o) => o[idField]);
    setForm((f) => ({ ...f, [field]: f[field].length === all.length ? [] : all }));
  };

  // ── Editar grupo (OS + TipoEstudio + Plan) ──
  const abrirEditarGrupo = async (os, grupo) => {
    const planesRes = await obrasSocialesAPI.getPlanesPorObraSocial(os.idObraSocial);
    setPlanes(planesRes.data || []);
    setForm({
      idObraSocial: os.idObraSocial,
      idTiposEstudio: [grupo.idTipoEstudio],
      idRequisitos: grupo.requisitos.map((r) => r.idRequisito),
      idPlanes: grupo.idPlan ? [grupo.idPlan] : [],
      adjuntoObligatorio: grupo.requisitos[0]?.adjuntoObligatorio ?? false,
      _editIds: grupo.requisitos.map((r) => r.id),
      _editObraSocial: os.obraSocial,
      _editTipoEstudio: grupo.idTipoEstudio,
      _editPlan: grupo.idPlan ?? '',
    });
    setModal({ mode: 'editar', os, grupo });
  };

  const ejecutarGuardado = async () => {
    try {
      if (modal.mode === 'editar') {
        // Requisitos actuales en el grupo
        const reqActualesIds = modal.grupo.requisitos.map((r) => r.idRequisito);
        const reqNuevosIds = form.idRequisitos;

        // Eliminar los que se quitaron
        const aEliminar = modal.grupo.requisitos.filter((r) => !reqNuevosIds.includes(r.idRequisito));
        await Promise.all(aEliminar.map((r) => eliminar(r.id)));

        // Agregar los nuevos
        const aAgregar = reqNuevosIds.filter((id) => !reqActualesIds.includes(id));
        if (aAgregar.length > 0) {
          await guardar({
            idObraSocial: form.idObraSocial,
            idRequisitos: aAgregar,
            idTiposEstudio: [form._editTipoEstudio],
            idPlanes: form._editPlan ? [Number(form._editPlan)] : [null],
            adjuntoObligatorio: form.adjuntoObligatorio,
          });
        }

        // Actualizar adjunto en los que quedan
        const quedan = modal.grupo.requisitos.filter((r) => reqNuevosIds.includes(r.idRequisito));
        await Promise.all(quedan.map((r) => actualizar(r.id, {
          adjuntoObligatorio: form.adjuntoObligatorio,
          idRequisito: r.idRequisito,
          idTipoEstudio: form._editTipoEstudio,
          idPlan: form._editPlan || null,
        })));

        setToast({ message: 'Grupo actualizado con éxito.', type: 'success' });
      } else {
        await guardar({
          idObraSocial: Number(form.idObraSocial),
          idRequisitos: form.idRequisitos,
          idTiposEstudio: form.idTiposEstudio,
          idPlanes: form.idPlanes.length > 0 ? form.idPlanes : [null],
          adjuntoObligatorio: form.adjuntoObligatorio,
        });
        setToast({ message: 'Requisitos guardados con éxito.', type: 'success' });
      }
      setModal(null);
      setConfirmPisar(null);
      aplicarFiltros(filtros);
    } catch {
      setToast({ message: 'Error al guardar.', type: 'error' });
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (modal.mode === 'nuevo') {
      const existentes = detectarExistentes();
      if (existentes.length > 0) {
        const cantidad = existentes.length;
        setConfirmPisar({
          message: `Se encontraron ${cantidad} requisito(s) que ya existen para las combinaciones seleccionadas (plan + tipo de estudio). Se van a pisar. ¿Desea continuar?`,
          onConfirm: () => ejecutarGuardado()
        });
        return;
      }
    }
    await ejecutarGuardado();
  };

  const confirmarEliminarFn = async () => {
    try {
      await Promise.all(confirmEliminar.ids.map((id) => eliminar(id)));
      setToast({ message: 'Eliminado con éxito.', type: 'success' });
      aplicarFiltros(filtros);
    } catch {
      setToast({ message: 'Error al eliminar.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const isNuevoValid = form.idObraSocial && form.idRequisitos.length > 0 && form.idTiposEstudio.length > 0;

  const LABELS = { idRequisitos: 'Requisitos *', idTiposEstudio: 'Tipos de Estudio *', idPlanes: 'Planes' };

  const CheckboxList = ({ field, opts, idField, labelField, disabledIds }) => {
    const scrollRef = React.useRef(null);
    const scrollPos = React.useRef(0);

    React.useEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollPos.current;
    });

    const handleScroll = () => {
      if (scrollRef.current) scrollPos.current = scrollRef.current.scrollTop;
    };

    return (
      <div style={{ width: '100%', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
          <label style={{ display: 'block', fontWeight: 600, margin: 0 }}>{LABELS[field]}</label>
          <Button type="button" className="btn btn-link btn-sm p-0" onClick={() => toggleTodos(field, opts.filter(o => !disabledIds?.has(o[idField])), idField)}>
            {form[field].length === opts.filter(o => !disabledIds?.has(o[idField])).length ? 'Ninguno' : 'Todos'}
          </Button>
        </div>
        <div ref={scrollRef} onScroll={handleScroll} style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '0.75rem' }}>
          {opts.map((o) => {
            const disabled = disabledIds?.has(o[idField]);
            return (
              <div key={o[idField]} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.3rem 0', borderBottom: '1px solid #f0f0f0', opacity: disabled ? 0.45 : 1 }}>
                <input type="checkbox" id={`${field}-${o[idField]}`}
                  checked={disabled || form[field].includes(o[idField])}
                  disabled={disabled}
                  onChange={() => !disabled && toggleMulti(field, o[idField])}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginTop: '3px', flexShrink: 0 }} />
                <label htmlFor={`${field}-${o[idField]}`} style={{ margin: 0, lineHeight: '1.4', wordBreak: 'break-word', cursor: disabled ? 'default' : 'pointer' }}>
                  {o[labelField]}{disabled ? ' (ya existe)' : ''}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Assignment sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Requisitos por Obra Social
          </h2>
          <span className="page-subtitle">Gestión de requisitos por obra social, plan y tipo de estudio</span>
        </div>

        {/* Modal */}
        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg" enforceFocus={false}>
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? `Editar — ${modal.os?.obraSocial}` : 'Nuevo Requisito'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.5rem' }} ref={modalBodyRef}>
              {modal?.mode === 'nuevo' ? (
                <div style={{ width: '100%', marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>Obra Social *</label>
                  <select className="form-control" value={form.idObraSocial} onChange={(e) => handleObraSocialChange(e.target.value)} required>
                    <option value="">Seleccionar...</option>
                    {obrasSociales.map((os) => <option key={os.idObraSocial} value={os.idObraSocial}>{os.obraSocial}</option>)}
                  </select>
                </div>
              ) : (
                <div style={{ width: '100%', marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>Tipo de Estudio</label>
                  <p style={{ margin: 0, fontWeight: 500 }}>{modal?.grupo?.tipoEstudio}</p>
                </div>
              )}

              {planes.length > 0 && modal?.mode === 'nuevo' && (
                <CheckboxList field="idPlanes" opts={planes} idField="idPlan" labelField="plans" />
              )}

              {modal?.mode === 'nuevo' && (
                <CheckboxList field="idTiposEstudio" opts={tiposEstudioDisponibles} idField="idTipoEstudio" labelField="tipoEstudio" />
              )}

              <CheckboxList field="idRequisitos" opts={requisitosOpts} idField="idRequisito" labelField="requisito1" />

              <div style={{ width: '100%', marginTop: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0' }}>
                  <input type="checkbox" id="adjunto-obligatorio" checked={form.adjuntoObligatorio}
                    onChange={(e) => setForm((f) => ({ ...f, adjuntoObligatorio: e.target.checked }))} style={{ flexShrink: 0 }} />
                  <label htmlFor="adjunto-obligatorio" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Adjunto Obligatorio</label>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || (modal?.mode === 'nuevo' && !isNuevoValid)}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Filtros */}
        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtros.idObraSocial ? 'filter-active' : ''}`}>
                <label className="filter-label">Obra Social</label>
                <select name="idObraSocial" className="filter-input" value={filtros.idObraSocial} onChange={handleFiltroChange}>
                  <option value="">Todas</option>
                  {obrasSociales.map((os) => <option key={os.idObraSocial} value={os.idObraSocial}>{os.obraSocial}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtros.idRequisito ? 'filter-active' : ''}`}>
                <label className="filter-label">Requisito</label>
                <select name="idRequisito" className="filter-input" value={filtros.idRequisito} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  {requisitosOpts.map((r) => <option key={r.idRequisito} value={r.idRequisito}>{r.requisito1 || r.requisito}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtros.idTipoEstudio ? 'filter-active' : ''}`}>
                <label className="filter-label">Tipo de Estudio</label>
                <select name="idTipoEstudio" className="filter-input" value={filtros.idTipoEstudio} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  {tiposEstudio.map((te) => <option key={te.idTipoEstudio} value={te.idTipoEstudio}>{te.tipoEstudio}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={handleBuscar} disabled={loading}><Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar</Button>
                <Button className="btn-clear" onClick={handleLimpiar}><Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Resultados */}
        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{requisitos.length} requisitos en {agrupado.length} obras sociales</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Requisito
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Obra Social</th>
                  <th style={{ width: '10%' }}>Plan</th>
                  <th style={{ width: '25%' }}>Tipo de Estudio</th>
                  <th>Requisitos</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {agrupado.length === 0 ? (
                  <tr><td colSpan={5} className="empty-state">{loading ? 'Cargando...' : 'No hay requisitos para mostrar'}</td></tr>
                ) : agrupado.map((os) =>
                  os.grupos.map((grupo, gi) => (
                    <tr key={`${os.idObraSocial}-${grupo.idTipoEstudio}-${grupo.idPlan}`}>
                      {gi === 0 && (
                        <td rowSpan={os.grupos.length} style={{ verticalAlign: 'middle', fontWeight: 600, borderRight: '2px solid #dee2e6', background: 'var(--bg-secondary, #f8f9fa)' }}>
                          {os.obraSocial}
                        </td>
                      )}
                      <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{grupo.plan || '-'}</td>
                      <td style={{ fontSize: '0.875rem' }}>{grupo.tipoEstudio}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {grupo.requisitos.map((r) => (
                            <span key={r.id} style={{
                              display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '12px',
                              fontSize: '0.75rem', fontWeight: 500, alignSelf: 'flex-start',
                              background: r.adjuntoObligatorio ? '#fef3c7' : '#e0f2fe',
                              color: r.adjuntoObligatorio ? '#92400e' : '#0369a1',
                              border: `1px solid ${r.adjuntoObligatorio ? '#fcd34d' : '#7dd3fc'}`,
                            }}>
                              {r.nombre}{r.adjuntoObligatorio ? ' 📎' : ''}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="action-cell">
                        <RowActions onEdit={() => abrirEditarGrupo(os, grupo)} onDelete={() => setConfirmEliminar({ ids: grupo.requisitos.map((r) => r.id), label: `${os.obraSocial} / ${grupo.tipoEstudio}` })} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar grupo de requisitos"
        message={`¿Eliminar todos los requisitos de "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminarFn}
        onCancel={() => setConfirmEliminar(null)}
      />
      <ConfirmModal
        show={confirmPisar !== null}
        title="Requisitos existentes"
        tone="warning"
        message={confirmPisar?.message}
        onConfirm={confirmPisar?.onConfirm}
        onCancel={() => setConfirmPisar(null)}
        centered
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaObraSocialRequisitos;
